// src/services/auth.service.js
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Capacitor } from '@capacitor/core';
import { signOut, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from "@aws-amplify/core"
import { getCredentialsProvider } from './CustomCredentialsProvider';

// Initialize social login providers
export const initializeSocialLogins = async () => {
  try {
    // Public provider identifiers come from the same Vite environment as the
    // app bundle; do not import Node/Capacitor build configuration into the UI.
    await SocialLogin.initialize({
      google: { webClientId: import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID },
      facebook: { appId: import.meta.env.VITE_FACEBOOK_APP_ID, clientToken: import.meta.env.VITE_FACEBOOK_CLIENT_TOKEN },
      apple: { clientId: import.meta.env.VITE_APPLE_CLIENT_ID, redirectUrl: import.meta.env.VITE_APPLE_REDIRECT_URL },
    });

    return { success: true };
  } catch (error) {
    console.error('Error initializing social logins', error);
    return { success: false, error: error.message };
  }
};

export const googleSignIn = async () => {
  try {
    
    // For web platform, use Amplify's built-in redirect flow
    if (!__IS_NATIVE__) {
      console.error('Initiating Google sign-in with build in redirect flow');
      // clear socialUserInfo from localStorage
      localStorage.removeItem('socialUserInfo');
      // This will redirect to Google login page
      await signInWithRedirect({
        provider: 'Google'
      });
      
      // The code below won't execute immediately due to redirect
      return {
        user: null,
        credentials: null,
        error: null
      };
    } 
    // For native platforms (iOS/Android)
    else {
 
     const domain = __PLATFORM__ === 'ios' ? 'ios.accounts.google.com' : 'accounts.google.com';

      // First get the token using the native SDK via Capgo's Social Login plugin
      const googleResponse = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile']
        }
      });



      if (!googleResponse.result || !googleResponse.result.idToken) {
        throw new Error('No ID token received from Google');
      }
      
      // Store the user profile information for later use by the auth store
      if (googleResponse.result.profile) {
        const userInfo = {
          name: googleResponse.result.profile.name,
          email: googleResponse.result.profile.email,
          picture: googleResponse.result.profile.imageUrl,
          id: googleResponse.result.profile.id,
          provider: 'google'
        };
        
        // Store in localStorage to be used by the auth store
        localStorage.setItem('socialUserInfo', JSON.stringify(userInfo));
      }
      
      // Cognito Identity Pool GetId for accounts.google.com requires the
      // Google OIDC id_token — the access token is rejected with a 400.
      getCredentialsProvider().loadFederatedLogin({
        domain: domain,
        token: googleResponse.result.idToken
      });
      
      try {
        // Fetch auth session to get AWS credentials
        const fetchSessionResult = await fetchAuthSession();

        // Trigger auth store update via Hub
        Hub.dispatch('auth', {
          event: 'socialSignIn',
          data: {
            provider: 'google',
            user: googleResponse.result.profile,
            session: fetchSessionResult
          }
        });
        
        return {
          user: googleResponse.result.profile,
          credentials: fetchSessionResult.credentials,
          identityId: fetchSessionResult.identityId,
          error: null
        };
      } catch (sessionError) {
        console.error('Error fetching auth session:', sessionError);
        
        // Retry once against a cleared provider state — still the OIDC
        // id_token; Cognito rejects the OAuth access token for GetId.
        if (!googleResponse.result.idToken) {
          throw new Error('No ID token available for retry');
        }

        getCredentialsProvider().clearCredentialsAndIdentityId();

        getCredentialsProvider().loadFederatedLogin({
          domain: domain,
          token: googleResponse.result.idToken
        });
        
        const fetchSessionResult = await fetchAuthSession();
        
        // Trigger auth store update via Hub
        Hub.dispatch('auth', {
          event: 'socialSignIn',
          data: {
            provider: 'google',
            user: googleResponse.result.profile,
            session: fetchSessionResult
          }
        });
        
        return {
          user: googleResponse.result.profile,
          credentials: fetchSessionResult.credentials,
          identityId: fetchSessionResult.identityId,
          error: null
        };
      }
    }
  } catch (error) {
    console.error('Error signing in with Google', error);
    return {
      user: null,
      credentials: null,
      error: error.message
    };
  }
};

// Facebook Sign-In
export const facebookSignIn = async () => {
  try {
    // For web platform, use Amplify's built-in redirect flow
    if (!__IS_NATIVE__) {
      // This will redirect to Facebook login page
      await signInWithRedirect({
        provider: 'Facebook'
      });
      
      // The code below won't execute immediately due to redirect
      return {
        user: null,
        credentials: null,
        error: null
      };
    } 
    // For native platforms (iOS/Android)
    else {
      // First get the token using the native SDK via Capgo's Social Login plugin
      const fbResponse = await SocialLogin.login({
        provider: 'facebook',
        options: {
          permissions: ['email', 'public_profile']
        }
      });

      // Never log fbResponse — it contains the Facebook access token + profile PII.

      if (!fbResponse.result || !fbResponse.result.accessToken || !fbResponse.result.accessToken.token) {
        throw new Error('No access token received from Facebook');
      }
      
      // Store the user profile information for later use by the auth store
      if (fbResponse.result.profile) {
        const userInfo = {
          name: fbResponse.result.profile.name,
          email: fbResponse.result.profile.email,
          picture: fbResponse.result.profile.imageUrl || fbResponse.result.profile.picture?.data?.url,
          id: fbResponse.result.profile.id,
          provider: 'facebook'
        };
        
        // Store in localStorage to be used by the auth store
        localStorage.setItem('socialUserInfo', JSON.stringify(userInfo));
      }
      
      // Load the federated login information into the custom provider
      getCredentialsProvider().loadFederatedLogin({
        domain: 'graph.facebook.com',
        token: fbResponse.result.accessToken.token
      });
      
      try {
        // Fetch auth session to get AWS credentials
        const fetchSessionResult = await fetchAuthSession();

        // Trigger auth store update via Hub
        Hub.dispatch('auth', {
          event: 'socialSignIn',
          data: {
            provider: 'facebook',
            user: fbResponse.result.profile,
            session: fetchSessionResult
          }
        });
        
        return {
          user: fbResponse.result.profile,
          credentials: fetchSessionResult.credentials,
          identityId: fetchSessionResult.identityId,
          error: null
        };
      } catch (sessionError) {
        console.error('Error fetching auth session:', sessionError);

        // Try alternate approach if the first one fails: clear previous
        // state and retry the federated login with the same token.
        getCredentialsProvider().clearCredentialsAndIdentityId();
        
        // Try with a different approach for the token if needed
        getCredentialsProvider().loadFederatedLogin({
          domain: 'graph.facebook.com',
          token: fbResponse.result.accessToken.token
        });
        
        const fetchSessionResult = await fetchAuthSession();
        
        // Trigger auth store update via Hub
        Hub.dispatch('auth', {
          event: 'socialSignIn',
          data: {
            provider: 'facebook',
            user: fbResponse.result.profile,
            session: fetchSessionResult
          }
        });
        
        return {
          user: fbResponse.result.profile,
          credentials: fetchSessionResult.credentials,
          identityId: fetchSessionResult.identityId,
          error: null
        };
      }
    }
  } catch (error) {
    console.error('Error signing in with Facebook', error);
    return {
      user: null,
      credentials: null,
      error: error.message
    };
  }
};
// Apple Sign-In
export const appleSignIn = async () => {
  try {
    // Using Capgo's Social Login plugin for Apple auth
    const response = await SocialLogin.login({
      provider: 'apple',
      options: {
        scopes: ['email', 'name']
      }
    });
    
    if (!response || !response.result || !response.result.idToken) {
      throw new Error('Apple login failed');
    }
    
    // Store the user profile information for later use by the auth store
    if (response.result.profile) {
      const userInfo = {
        name: [response.result.profile?.givenName, response.result.profile?.familyName].filter(Boolean).join(' ') || 'Apple User',
        email: response.result.profile?.email,
        picture: undefined,
        id: response.result.profile?.user,
        provider: 'apple'
      };
      localStorage.setItem('socialUserInfo', JSON.stringify(userInfo));
    }

    // Exchange the Apple id_token for AWS credentials via the custom
    // credentials provider. Cognito Identity Pool GetId accepts Apple logins
    // under the issuer domain appleid.apple.com — Amplify v6 signIn() has no
    // provider/tokens overload, so that call can never succeed.
    getCredentialsProvider().loadFederatedLogin({
      domain: 'appleid.apple.com',
      token: response.result.idToken
    });

    const fetchSessionResult = await fetchAuthSession();

    Hub.dispatch('auth', {
      event: 'socialSignIn',
      data: {
        provider: 'apple',
        user: response.result.profile,
        session: fetchSessionResult
      }
    });

    return {
      user: response.result.profile,
      credentials: fetchSessionResult.credentials,
      identityId: fetchSessionResult.identityId,
      error: null
    };
  } catch (error) {
    console.error('Error signing in with Apple', error);
    return {
      user: null,
      credentials: null,
      error: error.message
    };
  }
};

// Sign Out
export const signOutFunc = async () => {
  if (__IS_NATIVE__) {
    try {
      // Sign out from all social logins
      await SocialLogin.logout({ provider: 'google' });
    } catch (error) {
      console.debug('Error signing out from Google', error);
    }
    try {
      await SocialLogin.logout({ provider: 'facebook' });
    }
    catch (error) {
      console.debug('Error signing out from Facebook', error);
    }
    try {
      await SocialLogin.logout({ provider: 'apple' });
    }
    catch (error) {
      console.debug('Error signing out from Apple', error);
    }
  }
 
  try {
    // Sign out from AWS
    await signOut();
    return { success: true };
  } catch (error) {
    console.error('Error signing out', error);
    return { success: false, error: error.message };
  }
};